import { useCallback, useEffect, useMemo, useState } from "react"

export const notificationPreferenceOptions = [
  {
    id: "campaigns",
    title: "Campaigns and votes",
    description: "Open campaigns, followed campaign updates, and votes closing soon.",
  },
  {
    id: "messages",
    title: "Messages and replies",
    description: "Forum replies, direct chats, and conversations you are part of.",
  },
  {
    id: "marketplace",
    title: "Marketplace activity",
    description: "Trade offers, accepted trades, BTC purchase updates, and listing activity.",
  },
  {
    id: "groups",
    title: "Groups and memberships",
    description: "Friend requests, group updates, membership changes, and role activity.",
  },
  {
    id: "tasks",
    title: "Tasks and calendar",
    description: "Assigned tasks, due dates, meeting reminders, and scheduled events.",
  },
]

const buildDefaults = () =>
  notificationPreferenceOptions.reduce((current, option) => ({
    ...current,
    [option.id]: true,
  }), {})

const getStorageKey = (userId) => `tyk-notification-preferences:${userId || "anonymous"}`

export default function useNotificationPreferences(userId) {
  const storageKey = useMemo(() => getStorageKey(userId), [userId])
  const [preferences, setPreferences] = useState(() => buildDefaults())

  const readPreferences = useCallback(() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "{}")
      setPreferences({
        ...buildDefaults(),
        ...(parsed && typeof parsed === "object" ? parsed : {}),
      })
    } catch (error) {
      console.error("Error reading notification preferences:", error)
      setPreferences(buildDefaults())
    }
  }, [storageKey])

  useEffect(() => {
    readPreferences()

    const handleStorage = (event) => {
      if (event.key === storageKey) {
        readPreferences()
      }
    }

    window.addEventListener("storage", handleStorage)
    return () => window.removeEventListener("storage", handleStorage)
  }, [readPreferences, storageKey])

  const writePreferences = useCallback((nextPreferences) => {
    setPreferences(nextPreferences)
    window.localStorage.setItem(storageKey, JSON.stringify(nextPreferences))
    window.dispatchEvent(new Event("notification-preferences-updated"))
  }, [storageKey])

  const setPreference = useCallback((id, value) => {
    writePreferences({
      ...preferences,
      [id]: value,
    })
  }, [preferences, writePreferences])

  const enabledCount = useMemo(
    () => notificationPreferenceOptions.filter((option) => preferences[option.id]).length,
    [preferences],
  )

  return {
    enabledCount,
    options: notificationPreferenceOptions,
    preferences,
    setPreference,
  }
}
